/**
 * Calls to the HTTP API, with the access token attached.
 *
 * Here rather than at each call site so that token refresh and the response to a
 * rejected token are decided once. Everything authenticated goes through this.
 */
import { currentAccessToken, signOut } from './auth.js';

/** Read on use, like `auth.ts`, so importing this needs no configuration. */
const base = (): string => import.meta.env['VITE_API_BASE_URL'] ?? '';

export const isConfigured = (): boolean => base() !== '';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Raised when there is no usable token, so the UI can return to the sign-in form. */
export class NotSignedIn extends Error {
  constructor() {
    super('Please sign in again.');
    this.name = 'NotSignedIn';
  }
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  if (!isConfigured()) throw new ApiError(0, 'No API base URL configured for this build.');

  const token = await currentAccessToken();
  if (token === null) throw new NotSignedIn();

  let response: Response;
  try {
    response = await fetch(`${base()}${path}`, {
      ...init,
      headers: {
        ...(init.headers ?? {}),
        'content-type': 'application/json',
        // The raw token, not a `Bearer` prefix. API Gateway accepts either, and
        // this is the form verified end to end against the deployed authorizer.
        authorization: token,
      },
    });
  } catch {
    throw new ApiError(0, 'Could not reach the service.');
  }

  if (response.status === 401) {
    // The token was refused rather than merely expired, so the stored session is
    // no longer worth keeping; leaving it would retry on every request.
    signOut();
    throw new NotSignedIn();
  }

  const text = await response.text();
  const body = text === '' ? undefined : (JSON.parse(text) as unknown);

  if (!response.ok) {
    const message =
      body !== undefined && typeof (body as { message?: unknown }).message === 'string'
        ? (body as { message: string }).message
        : 'That request did not work.';
    throw new ApiError(response.status, message);
  }

  return body as T;
}
